import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallets } from '../WalletContext';
import api from '../services/api';
import './Home.css';

function Home() {
  const navigate = useNavigate();
  const { selectedWallets } = useWallets();
  const currentYear = new Date().getFullYear();
  const [loading, setLoading] = useState(true);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [timeOffBalance, setTimeOffBalance] = useState(null);
  const [timeOffYear, setTimeOffYear] = useState(new Date().getFullYear());
  const [timeOffLoading, setTimeOffLoading] = useState(false);
  const [upcomingTimeOff, setUpcomingTimeOff] = useState([]);
  const [householdDebts, setHouseholdDebts] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [userName, setUserName] = useState('');
  const [currentJob, setCurrentJob] = useState(null); // For fiscal year period display

  const currentMonth = new Date().toLocaleString('en-GB', { month: 'long', year: 'numeric' });

  useEffect(() => {
    fetchAllData();
  }, [selectedWallets]);

  // Re-fetch time off balance when year changes
  useEffect(() => {
    if (!loading) {
      fetchTimeOffBalance(timeOffYear);
    }
  }, [timeOffYear]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchProfile(),
        fetchFinancialSummary(),
        fetchTimeOffData(),
        fetchHouseholdDebts(),
        fetchRecentTransactions()
      ]);
    } catch (err) {
      console.error('Error fetching home data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const res = await api.get('/auth/profile');
      setUserName(res.data.display_name || res.data.name || 'there');
    } catch (err) {
      console.error('Error fetching profile:', err);
    }
  };

  const fetchFinancialSummary = async () => {
    try {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
      
      const walletParams = selectedWallets?.length > 0 
        ? `&wallet_ids=${selectedWallets.join(',')}` 
        : '';
      
      const res = await api.get(`/personal/summary?start_date=${startOfMonth}&end_date=${endOfMonth}${walletParams}`);
      setFinancialSummary(res.data);
    } catch (err) {
      console.error('Error fetching financial summary:', err);
      setFinancialSummary({ income: 0, expenses: 0, net: 0 });
    }
  };

  const fetchTimeOffData = async () => {
    try {
      const year = timeOffYear;
      
      // Fetch balance, calendar entries and employment data (same as TimeOff.jsx)
      const [balanceRes, calendarRes, jobsRes] = await Promise.all([
        api.get(`/timeoff/balance?year=${year}`),
        api.get(`/timeoff/calendar/${year}`),
        api.get('/employment/jobs').catch(() => ({ data: [] }))
      ]);
      
      // Set current job for period display
      const jobs = jobsRes.data || [];
      const activeJob = jobs.find(j => j.is_current) || jobs[0] || null;
      setCurrentJob(activeJob);
      
      const balance = balanceRes.data;
      const entries = calendarRes.data?.entries || [];
      const today = new Date().toISOString().split('T')[0];
      
      // Get upcoming entries for display
      const upcomingEntries = entries
        .filter(e => e.start_date > today && (e.status === 'approved' || e.status === 'pending'))
        .sort((a, b) => a.start_date.localeCompare(b.start_date))
        .slice(0, 3);
      
      setTimeOffBalance({
        configured: balance.configured !== false,
        total_days: balance.total_days || 0,
        used_days: balance.used_days || 0,
        planned_days: balance.planned_days || 0,
        available_days: balance.available_days || 0,
        year_type: activeJob?.year_type || 'calendar',
        fiscal_year_start: activeJob?.fiscal_year_start
      });
      setUpcomingTimeOff(upcomingEntries);
    } catch (err) {
      // 404 means not configured, which is ok
      console.log('Time off not configured yet');
      setTimeOffBalance({ configured: false });
    }
  };

  const fetchTimeOffBalance = async (year) => {
    setTimeOffLoading(true);
    try {
      const [balanceRes, calendarRes] = await Promise.all([
        api.get(`/timeoff/balance?year=${year}`),
        api.get(`/timeoff/calendar/${year}`)
      ]);
      const balance = balanceRes.data;
      const entries = calendarRes.data?.entries || [];
      const today = new Date().toISOString().split('T')[0];
      
      const upcomingEntries = entries
        .filter(e => e.start_date > today && (e.status === 'approved' || e.status === 'pending'))
        .sort((a, b) => a.start_date.localeCompare(b.start_date))
        .slice(0, 3);
      
      setTimeOffBalance(prev => ({
        ...prev,
        configured: true, // Keep configured if user already set up
        total_days: balance.total_days || 0,
        used_days: balance.used_days || 0,
        planned_days: balance.planned_days || 0,
        available_days: balance.available_days || 0,
      }));
      setUpcomingTimeOff(upcomingEntries);
    } catch (err) {
      console.log('Time off balance fetch failed for year:', year);
      setTimeOffBalance(prev => ({
        ...prev,
        configured: prev?.configured ?? false,
        total_days: 0,
        used_days: 0,
        planned_days: 0,
        available_days: 0
      }));
    } finally {
      setTimeOffLoading(false);
    }
  };

  const fetchHouseholdDebts = async () => {
    try {
      const householdsRes = await api.get('/household/');
      const households = householdsRes.data || [];
      
      const debts = [];
      for (const h of households.slice(0, 2)) {
        try {
          const summaryRes = await api.get(`/household/${h.id}/summary`);
          if (summaryRes.data.your_balance !== 0) {
            debts.push({
              household_name: h.name,
              balance: summaryRes.data.your_balance
            });
          }
        } catch (err) {
          // Skip this household
        }
      }
      setHouseholdDebts(debts);
    } catch (err) {
      console.error('Error fetching household debts:', err);
    }
  };

  const fetchRecentTransactions = async () => {
    try {
      const walletParams = selectedWallets?.length > 0 
        ? `?wallet_ids=${selectedWallets.join(',')}` 
        : '';
      const res = await api.get(`/personal/expenses${walletParams}`);
      setRecentTransactions((res.data || []).slice(0, 5));
    } catch (err) {
      console.error('Error fetching recent transactions:', err);
    }
  };

  const formatCurrency = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short' 
    });
  };

  // Helper to get leave year period title based on employment
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const getLeaveYearPeriod = () => {
    const yearType = timeOffBalance?.year_type || 'calendar';
    const fiscalStart = timeOffBalance?.fiscal_year_start;
    
    if (yearType === 'calendar' || !fiscalStart) {
      return `Jan - Dec ${timeOffYear}`;
    }
    
    const fiscalDate = new Date(fiscalStart);
    const startMonth = fiscalDate.getMonth();
    const today = new Date();
    const selectedYearFiscalStart = new Date(timeOffYear, startMonth, fiscalDate.getDate());
    
    let startYear, endYear;
    if (timeOffYear === currentYear) {
      if (today >= selectedYearFiscalStart) {
        startYear = timeOffYear;
        endYear = timeOffYear + 1;
      } else {
        startYear = timeOffYear - 1;
        endYear = timeOffYear;
      }
    } else {
      startYear = timeOffYear;
      endYear = timeOffYear + 1;
    }
    
    const startMonthName = MONTHS[startMonth];
    const endMonth = startMonth === 0 ? 11 : startMonth - 1;
    const endMonthName = MONTHS[endMonth];
    
    return `${startMonthName} ${startYear} - ${endMonthName} ${endYear}`;
  };

  if (loading) {
    return (
      <div className="home-page">
        <div className="loading-state">
          <div className="loading-spinner" />
          <p>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="home-page">
      {/* Header */}
      <header className="home-header">
        <div className="greeting">
          <h1>👋 Hello, {userName}!</h1>
          <p className="date-display">{currentMonth}</p>
        </div>
      </header>

      {/* Main Grid */}
      <div className="widgets-grid">
        
        {/* Financial Summary Widget */}
        <div className="widget widget-financial">
          <div className="widget-header">
            <span className="widget-icon">💰</span>
            <span className="widget-title">This Month</span>
            <button className="widget-action" onClick={() => navigate('/analytics')}>
              View Details →
            </button>
          </div>
          <div className="financial-stats">
            <div className="fin-stat income">
              <span className="fin-label">Income</span>
              <span className="fin-value positive">
                {formatCurrency(financialSummary?.income || 0)}
              </span>
            </div>
            <div className="fin-stat expenses">
              <span className="fin-label">Expenses</span>
              <span className="fin-value negative">
                {formatCurrency(financialSummary?.expenses || 0)}
              </span>
            </div>
            <div className="fin-stat net">
              <span className="fin-label">Net</span>
              <span className={`fin-value ${(financialSummary?.net || 0) >= 0 ? 'positive' : 'negative'}`}>
                {formatCurrency(financialSummary?.net || 0)}
              </span>
            </div>
          </div>
        </div>

        {/* Time Off Widget */}
        <div className={`widget widget-timeoff ${timeOffLoading ? 'widget-loading' : ''}`}>
          <div className="widget-header">
            <span className="widget-icon">🏖️</span>
            <span className="widget-title">Time Off</span>
            <button className="widget-action" onClick={() => navigate('/timeoff')}>
              View Calendar →
            </button>
          </div>
          {timeOffBalance?.configured ? (
            <div className="timeoff-content">
              <div className="timeoff-period-nav">
                <button 
                  className="timeoff-nav-btn" 
                  onClick={() => setTimeOffYear(y => y - 1)}
                >‹</button>
                <div className={`timeoff-period ${timeOffYear !== currentYear ? 'not-current' : ''}`}>
                  {getLeaveYearPeriod()}
                </div>
                <button 
                  className="timeoff-nav-btn" 
                  onClick={() => setTimeOffYear(y => y + 1)}
                >›</button>
                {timeOffYear !== currentYear && (
                  <button 
                    className="timeoff-today-btn" 
                    onClick={() => setTimeOffYear(currentYear)}
                  >Now</button>
                )}
              </div>
              <div className="timeoff-balance">
                <div className="balance-circle">
                  <span className="balance-number">{timeOffBalance.available_days}</span>
                  <span className="balance-unit">days left</span>
                </div>
                <div className="balance-details">
                  <div className="detail-row">
                    <span>Used</span>
                    <span>{timeOffBalance.used_days} days</span>
                  </div>
                  <div className="detail-row">
                    <span>Planned</span>
                    <span>{timeOffBalance.planned_days} days</span>
                  </div>
                  <div className="detail-row total">
                    <span>Total</span>
                    <span>{timeOffBalance.total_days} days</span>
                  </div>
                </div>
              </div>
              {upcomingTimeOff.length > 0 && (
                <div className="upcoming-leave">
                  <span className="upcoming-label">Next:</span>
                  <span className="upcoming-dates">
                    {formatDate(upcomingTimeOff[0].start_date)} - {formatDate(upcomingTimeOff[0].end_date)}
                  </span>
                  <span className="upcoming-type">{upcomingTimeOff[0].leave_type_name}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="timeoff-empty">
              <p>Configure your leave allowance to start tracking</p>
              <button className="btn-configure" onClick={() => navigate('/timeoff')}>
                Set Up Time Off
              </button>
            </div>
          )}
        </div>

        {/* Household Debts Widget */}
        <div className="widget widget-debts">
          <div className="widget-header">
            <span className="widget-icon">🏠</span>
            <span className="widget-title">Household Balance</span>
            <button className="widget-action" onClick={() => navigate('/households')}>
              View All →
            </button>
          </div>
          {householdDebts.length > 0 ? (
            <div className="debts-list">
              {householdDebts.map((debt, i) => (
                <div key={i} className="debt-item">
                  <span className="debt-household">{debt.household_name}</span>
                  <span className={`debt-amount ${debt.balance > 0 ? 'owed-to-you' : 'you-owe'}`}>
                    {debt.balance > 0 ? '+' : ''}{formatCurrency(debt.balance)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="debts-empty">
              <span className="empty-icon">✓</span>
              <p>All settled! No outstanding debts.</p>
            </div>
          )}
        </div>

        {/* Recent Transactions Widget */}
        <div className="widget widget-transactions">
          <div className="widget-header">
            <span className="widget-icon">📋</span>
            <span className="widget-title">Recent Transactions</span>
            <button className="widget-action" onClick={() => navigate('/transactions')}>
              View All →
            </button>
          </div>
          {recentTransactions.length > 0 ? (
            <div className="transactions-list">
              {recentTransactions.map((tx, i) => (
                <div key={i} className="transaction-item">
                  <div className="tx-info">
                    <span className="tx-category">{tx.category}</span>
                    <span className="tx-description">{tx.description || '-'}</span>
                  </div>
                  <div className="tx-right">
                    <span className={`tx-amount ${tx.expense_type === 'income' ? 'positive' : 'negative'}`}>
                      {tx.expense_type === 'income' ? '+' : '-'}{formatCurrency(Math.abs(tx.amount))}
                    </span>
                    <span className="tx-date">{formatDate(tx.date)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="transactions-empty">
              <p>No recent transactions</p>
            </div>
          )}
        </div>

      </div>

      {/* Quick Actions */}
      <div className="quick-actions-bar">
        <button className="quick-action income" onClick={() => window.dispatchEvent(new CustomEvent('openAddIncome'))}>
          <span>💰</span> Add Income
        </button>
        <button className="quick-action expense" onClick={() => window.dispatchEvent(new CustomEvent('openAddExpense'))}>
          <span>💸</span> Add Expense
        </button>
        <button className="quick-action timeoff" onClick={() => navigate('/timeoff')}>
          <span>🏖️</span> Request Time Off
        </button>
      </div>
    </div>
  );
}

export default Home;
